import * as React from 'react';

import { cn } from '../../lib/utils';

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => {
    return (
      // `dir="auto"` (overridable via props) makes typed content self-orient:
      // a Hebrew note lays out RTL, an English/code value LTR, regardless of the
      // document's RTL base. Callers needing a fixed direction can pass `dir`.
      <textarea
        // bidi-auto-ok: live typing — the browser re-evaluates per keystroke.
        dir="auto"
        // `touch:min-h-[80px]` repeats the base floor rather than raising it: the
        // 80px here is already comfortably over the 44px coarse-pointer minimum, so
        // this changes no pixel on any pointer. What it changes is who can WIN
        // against it. The unprefixed `min-h-[80px]` loses a `tailwind-merge`
        // conflict to a caller's own unprefixed `min-h-*` outright — so a plausible
        // `<Textarea className="min-h-[40px]" />` compact composer would silently
        // drop this control under the floor on a finger as well as a mouse, with
        // `tools/check_touch_target_floor.py` unable to see it (a `min-h` alone
        // does not PIN a textarea, which grows with its content, so the scanner
        // correctly declines to call it short). A `touch:`-scoped copy survives that
        // merge and keeps the coarse-pointer floor whatever the caller writes,
        // which is what lets the gate exempt `Textarea`'s callers honestly. Same
        // property its six form-control siblings get from `touch:min-h-[44px]`.
        className={cn(
          'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 touch:min-h-[80px]',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
